<template>
	<v-container style="background-color: #ffffff">
		<div class="pb-6 text-right">
			<v-btn
				color="orangeButton"
				class="white text-h7"
				target="_blank"
			>
			<router-link style="text-decoration: none; color: white"
				:to="{
					path: '/knowledge',
					query: {
						article: 'Sustainability Tutorial',
						doc: 'value-chain'
					}
				}"
				target="_blank"> Value Chain Guide <v-icon class="pl-2">fas fa-external-link-alt</v-icon>
			</router-link>
			</v-btn>
		</div>
		<v-card class="pa-6 mb-6" elevation="0" outlined>
			<div class="pb-6 text-right">
				<v-btn
					color="primary"
					style="border-radius: 3px"
					@click="showEditDialog"
				>
					<v-icon small class="pr-3">fas fa-edit</v-icon>
					EDIT
				</v-btn>
				<v-btn
					color="primary ml-5"
					style="border-radius: 3px"
					@click="downloadValueChain(valueChainID)"
				>
					<v-icon left small>fas fa-download</v-icon>
					Download
				</v-btn>
				<v-btn
					color="primary ml-5"
					style="border-radius: 3px"
					@click="viewHistory"
				>
					VIEW EDIT HISTORY
				</v-btn>
			</div>
			<div class="value-chain-container">
				<div class="section-label">SUPPLIERS</div>

				<div class="chain-boxes">
					<!-- Supplier Tiers -->
					<v-card 
						v-for="tier in supplierTiers"
						:key="'supplier-tier-' + tier"
						class="tier-box" 
						outlined
					>
						<div class="tier-title">{{ tier }} tier</div>
						<div v-if="suppliers['tier' + tier] && suppliers['tier' + tier].length > 0">
							<div 
								v-for="(item, index) in suppliers['tier' + tier]"
								:key="'st-' + tier + '-' + index" 
								class="mb-2"
							>
								<div class="info-text">{{ item }}</div>
							</div>
						</div>
						<div v-else class="empty-text">No data available</div>
					</v-card>

					<!-- Company Box -->
					<v-card class="tier-box company-box" color="#2d9b9b">
						<div class="company-title">The company</div>
						<div class="company-content">
							<div class="company-section">
								<strong>Product/Service:</strong>
								<div class="info-text white--text">{{ company.product || 'N/A' }}</div>
							</div>
							<div class="company-section mt-3">
								<strong>Business Model:</strong>
								<div class="info-text white--text">{{ company.businessModel || 'N/A' }}</div>
							</div>
						</div>
					</v-card>

					<!-- Customer Tiers -->
					<v-card 
						v-for="tier in customerTiers"
						:key="'customer-tier-' + tier"
						class="tier-box" 
						outlined
					>
						<div class="tier-title">{{ tier }} tier</div>
						<div v-if="customers['tier' + tier] && customers['tier' + tier].length > 0">
							<div 
								v-for="(item, index) in customers['tier' + tier]"
								:key="'ct-' + tier + '-' + index"
								class="mb-2"
							>
								<div class="info-text">{{ item }}</div>
							</div>
						</div>
						<div v-else class="empty-text">No data available</div>
					</v-card>
				</div>

				<div class="section-label-right">CUSTOMERS</div>
			</div>

			<v-card class="info-section mb-3" outlined>
				<v-row no-gutters>
					<v-col cols="6" class="pa-4 border-right">
						<div class="section-heading">Raw materials</div>
						<div v-if="rawMaterials.length > 0">
							<div v-for="(item, index) in rawMaterials" :key="'rm-' + index" class="mb-2">
								<div class="info-text">{{ item }}</div>
							</div>
						</div>
						<div v-else class="empty-text">No data available</div>
					</v-col>
					<v-col cols="6" class="pa-4">
						<div class="section-heading">End user and End of Life</div>
						<div v-if="endUser.length > 0">
							<div v-for="(item, index) in endUser" :key="'eu-' + index" class="mb-2">
								<div class="info-text">{{ item }}</div>
							</div>
						</div>
						<div v-else class="empty-text">No data available</div>
					</v-col>
				</v-row>
			</v-card>

			<v-card class="info-section mb-3" outlined>
				<v-row no-gutters>
					<v-col cols="6" class="pa-4 border-right">
						<div class="section-heading">Factors the company impact</div>
						
						<div class="factor-subsection">
							<div class="subsection-title positive">
								<span class="factor-icon">+</span> Positive
							</div>
							<div v-if="factorsImpact.positive && factorsImpact.positive.length > 0">
								<div v-for="(item, index) in factorsImpact.positive" :key="'fip-' + index" class="mb-2">
									<div class="info-text">{{ item }}</div>
								</div>
							</div>
							<div v-else class="empty-text">No data available</div>
						</div>

						<div class="factor-subsection mt-4">
							<div class="subsection-title negative">
								<span class="factor-icon">-</span> Negative
							</div>
							<div v-if="factorsImpact.negative && factorsImpact.negative.length > 0">
								<div v-for="(item, index) in factorsImpact.negative" :key="'fin-' + index" class="mb-2">
									<div class="info-text">{{ item }}</div>
								</div>
							</div>
							<div v-else class="empty-text">No data available</div>
						</div>
					</v-col>

					<v-col cols="6" class="pa-4">
						<div class="section-heading">Factors impacting the company</div>
						
						<div class="factor-subsection">
							<div class="subsection-title positive">
								<span class="factor-icon">+</span> Positive
							</div>
							<div v-if="factorsImpacting.positive && factorsImpacting.positive.length > 0">
								<div v-for="(item, index) in factorsImpacting.positive" :key="'ficp-' + index" class="mb-2">
									<div class="info-text">{{ item }}</div>
								</div>
							</div>
							<div v-else class="empty-text">No data available</div>
						</div>

						<div class="factor-subsection mt-4">
							<div class="subsection-title negative">
								<span class="factor-icon">-</span> Negative
							</div>
							<div v-if="factorsImpacting.negative && factorsImpacting.negative.length > 0">
								<div v-for="(item, index) in factorsImpacting.negative" :key="'ficn-' + index" class="mb-2">
									<div class="info-text">{{ item }}</div>
								</div>
							</div>
							<div v-else class="empty-text">No data available</div>
						</div>
					</v-col>
				</v-row>
			</v-card>

			<v-card class="info-section mb-3" outlined>
				<v-row no-gutters>
					<v-col cols="6" class="pa-4 border-right">
						<div class="section-heading">Risks</div>
						<div v-if="risks.length > 0">
							<div v-for="(item, index) in risks" :key="'risk-' + index" class="mb-2">
								<div class="info-text">{{ item }}</div>
							</div>
						</div>
						<div v-else class="empty-text">No data available</div>
					</v-col>
					<v-col cols="6" class="pa-4">
						<div class="section-heading">Opportunities</div>
						<div v-if="opportunities.length > 0">
							<div v-for="(item, index) in opportunities" :key="'opp-' + index" class="mb-2">
								<div class="info-text">{{ item }}</div>
							</div>
						</div>
						<div v-else class="empty-text">No data available</div>
					</v-col>
				</v-row>
			</v-card>

			<v-card class="info-section" outlined>
				<div class="pa-4">
					<div class="section-heading">Stakeholders</div>
					<v-row v-if="stakeholders.length > 0">
						<v-col 
							v-for="(item, index) in stakeholders"
							:key="'stake-' + index"
							cols="12"
							md="4"
						>
							<div class="info-text">{{ item }}</div>
						</v-col>
					</v-row>
					<div v-else class="empty-text">No data available</div>
				</div>
			</v-card>
		</v-card>
		<v-dialog v-model="dialogEdit" max-width="500px">
			<v-card>
				<v-card-title class="text-center justify-center py-6">
					Do you want to Edit this Value Chain Details?
				</v-card-title>
				<v-card-actions class="justify-center pb-6">
					<v-btn outlined color="error" text @click="dialogEdit = false">Close</v-btn>
					<v-btn outlined color="primary" text @click="goToEditMode">Edit</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<v-dialog v-model="dialogHistory" max-width="600px">
			<v-card>
				<v-card-actions>
					<v-spacer />
					<v-btn icon color="primary" @click="dialogHistory = false">
						<v-icon large>fas fa-times</v-icon>
					</v-btn>
				</v-card-actions>
				<v-card-title class="text-center justify-center">
					Value Chain Edit History
				</v-card-title>
				<v-card-text class="text-center">
					<p v-for="(item, index) in history" :key="'history-' + index">
						<b>{{ item.EditUsername }}</b> edited this value chain at
						{{ new Date(item.Timestamp).toLocaleDateString() }}
						{{ new Date(item.Timestamp).toLocaleTimeString() }}
					</p>
					<p v-if="history.length === 0" class="empty-text">
						No edit history available
					</p>
				</v-card-text>
			</v-card>
		</v-dialog>
	</v-container>
</template>

<script>
import LoadingState from "../consts/LoadingState"
import CsrAPI from "../api/csr/csr"
import { mapGetters } from "vuex"

export default {
	name: "Value-Chain-Result",
	data() {
		return {
			suppliers: {},
			customers: {},
			company: { 
				product: "", 
				businessModel: "" 
			},
			rawMaterials: [],
			endUser: [],
			factorsImpact: { 
				positive: [], 
				negative: [] 
			},
			factorsImpacting: { 
				positive: [], 
				negative: [] 
			},
			risks: [],
			opportunities: [],
			stakeholders: [],
			dialogEdit: false,
			dialogHistory: false,
			valueChainID: null,
			history: []
		}
	},
	mounted() {
		this.valueChainID = parseInt(this.$route.params.id)
		this.loadValueChainData()
	},
	computed: {
		...mapGetters("user", ["isLoggedIn", "getUser"]),
		username() {
			return this.getUser.clientName
		},
		supplierTiers() {
			const tiers = Object.keys(this.suppliers)
				.filter(key => key.startsWith("tier"))
				.map(key => parseInt(key.replace("tier", "")))
				.sort((a, b) => b - a) // Sort descending (5, 4, 3, 2, 1)
			return tiers
		},
		// Dynamic customer tiers (sorted in ascending order for display)
		customerTiers() {
			const tiers = Object.keys(this.customers)
				.filter(key => key.startsWith("tier"))
				.map(key => parseInt(key.replace("tier", "")))
				.sort((a, b) => a - b) // Sort ascending (1, 2, 3, 4, 5)
			return tiers
		}
	},
	methods: {
		showEditDialog() {
			this.dialogEdit = !this.dialogEdit
		},
		viewHistory() {
			this.dialogHistory = true
			this.loadEditHistory()
		},
		goToEditMode() {
			this.$router.push({ 
				path: "/csr/esgscore/valuechain", 
				query: { edit: true } 
			})
		},
		loadValueChainData() {
			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				null,
				{ root: true }
			)
			CsrAPI.getExistingValueChain()
				.then(result => {
					
					if (result && result.length > 0) {
						const data = result[0]

						if (data.Suppliers) {
							const suppliers = typeof data.Suppliers === "string" 
								? JSON.parse(data.Suppliers) 
								: data.Suppliers
							this.suppliers = suppliers
						}
						
						if (data.Customers) {
							const customers = typeof data.Customers === "string" 
								? JSON.parse(data.Customers) 
								: data.Customers
							this.customers = customers
						}
						
						this.company.product = data.Product || ""
						this.company.businessModel = data.Business_Model || ""
						
						if (data.Row_Materials) {
							this.rawMaterials = typeof data.Row_Materials === "string" 
								? JSON.parse(data.Row_Materials) 
								: data.Row_Materials
							this.rawMaterials = this.rawMaterials.filter(item => item !== "")
						}
						
						if (data.End_Users) {
							this.endUser = typeof data.End_Users === "string" 
								? JSON.parse(data.End_Users) 
								: data.End_Users
							this.endUser = this.endUser.filter(item => item !== "")
						}
						
						if (data.Factors_Impact) {
							this.factorsImpact = typeof data.Factors_Impact === "string" 
								? JSON.parse(data.Factors_Impact) 
								: data.Factors_Impact
							this.factorsImpact.positive = (this.factorsImpact.positive || []).filter(item => item !== "")
							this.factorsImpact.negative = (this.factorsImpact.negative || []).filter(item => item !== "")
						}
						
						if (data.Factors_Impacting) {
							this.factorsImpacting = typeof data.Factors_Impacting === "string" 
								? JSON.parse(data.Factors_Impacting) 
								: data.Factors_Impacting
							this.factorsImpacting.positive = (this.factorsImpacting.positive || []).filter(item => item !== "")
							this.factorsImpacting.negative = (this.factorsImpacting.negative || []).filter(item => item !== "")
						}
						
						if (data.Risks) {
							this.risks = typeof data.Risks === "string" 
								? JSON.parse(data.Risks) 
								: data.Risks
							this.risks = this.risks.filter(item => item !== "")
						}
						
						if (data.Opportunities) {
							this.opportunities = typeof data.Opportunities === "string" 
								? JSON.parse(data.Opportunities) 
								: data.Opportunities
							this.opportunities = this.opportunities.filter(item => item !== "")
						}
						
						if (data.Stakeholders) {
							this.stakeholders = typeof data.Stakeholders === "string" 
								? JSON.parse(data.Stakeholders) 
								: data.Stakeholders
							this.stakeholders = this.stakeholders.filter(item => item !== "")
						}
					}
					
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_END_LOADING,
						null,
						{ root: true }
					)
				})
				.catch(reason => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						reason,
						{ root: true }
					)
				})
		},
		downloadValueChain(valueChainID) {
			let name = "valuechain_for_"
			name += this.username
			name = name.split(" ").join("_")
			CsrAPI.downloadValueChainPDF(valueChainID, name)
		},
		loadEditHistory() {
			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				null,
				{ root: true }
			)
			CsrAPI.getEditHistory()
				.then(result => {
					
					this.history = result || [] 
					this.$store.dispatch("loading/" + LoadingState.ACTION_END_LOADING, null, { root: true })
				})
				.catch(reason => {
					this.$store.dispatch("loading/" + LoadingState.ACTION_SET_ERROR, reason, { root: true })
				})
		},
	}
}
</script>

<style scoped>
.value-chain-container {
	position: relative;
	margin: 30px 0;
}

.section-label {
	position: absolute;
	left: 0;
	top: -30px;
	font-weight: 600;
	font-size: 14px;
	color: #666;
	letter-spacing: 1px;
}

.section-label-right {
	position: absolute;
	right: 0;
	top: -30px;
	font-weight: 600;
	font-size: 14px;
	color: #666;
	letter-spacing: 1px;
}

.chain-boxes {
	display: flex;
	gap: 15px;
	width: 100%;
	justify-content: center;
}

.tier-box {
	flex: 0 0 calc((100% - 120px) / 6);
	min-height: 250px;
	padding: 20px;
	border: 2px solid #2d9b9b !important;
	border-radius: 20px !important;
	display: flex;
	flex-direction: column;
}

.company-box {
	color: white;
}

.tier-title {
	font-size: 16px;
	font-weight: 600;
	margin-bottom: 15px;
	text-align: center;
}

.company-title {
	font-size: 18px;
	font-weight: 600;
	margin-bottom: 15px;
	text-align: center;
}

.company-content {
	flex-grow: 1;
	display: flex;
	flex-direction: column;
}

.company-section {
	margin-bottom: 10px;
}

.info-text {
	padding: 8px 12px;
	background-color: #f5f5f5;
	border-radius: 4px;
	font-size: 14px;
	min-height: 40px;
	display: flex;
	align-items: center;
}

.white--text .info-text,
.company-box .info-text {
	background-color: rgba(255, 255, 255, 0.2);
	color: white;
}

.empty-text {
	color: #999;
	font-style: italic;
	padding: 8px;
	text-align: center;
}

.info-section {
	border-radius: 15px !important;
	border: 2px solid #2d9b9b !important;
}

.section-heading {
	font-size: 16px;
	font-weight: 600;
	margin-bottom: 15px;
	color: #333;
}

.border-right {
	border-right: 1px solid #e0e0e0;
}

.factor-subsection {
	margin-bottom: 10px;
}

.subsection-title {
	font-size: 14px;
	font-weight: 600;
	margin-bottom: 10px;
	color: #555;
	display: flex;
	align-items: center;
}

.factor-icon {
	font-size: 20px;
	font-weight: bold;
	margin-right: 10px;
	width: 25px;
}

.positive {
	color: #4caf50;
}

.negative {
	color: #f44336;
}

@media (max-width: 1200px) {
	.chain-boxes {
		flex-wrap: wrap;
	}
	
	.tier-box {
		min-width: 45%;
	}
}
</style>